import type { ListResult, QueryOptions } from "@/services/contracts";

/**
 * Shared pagination helper for Accounts (and other) list endpoints.
 *
 * Many API list endpoints page at 25 records by default, which can silently
 * hide records beyond page 1 when callers only request a single page. This
 * helper walks every page in order and returns the full, ordered item list.
 *
 * Usage:
 *   const payments = await loadAllPages((options) => accounts.listFarmerPayments(options));
 *   const balances = await loadAllPages((options) => accounts.listFarmerBalances(options), { status: "ACTIVE" }, (balance) => balance.farmerId);
 */
export const PAGE_ALL_PAGE_SIZE = 100;

/** Safety cap: never walk more than 1000 pages (~100k records) in one call. */
export const PAGE_ALL_MAX_PAGES = 1000;

export type PageLoader<T> = (options?: QueryOptions) => Promise<ListResult<T>>;

export type PageKeySelector<T> = (item: T) => string;

const defaultKeySelector = <T,>(item: T): string => {
  if (item && typeof item === "object") {
    const record = item as { id?: unknown; farmerId?: unknown };
    if (typeof record.id === "string" && record.id) return record.id;
    if (typeof record.farmerId === "string" && record.farmerId) return record.farmerId;
  }
  return "";
};

const declaredPageCount = (result: ListResult<unknown>): number | undefined => {
  const totalPages = result?.totalPages;
  if (typeof totalPages === "number" && Number.isFinite(totalPages) && totalPages >= 1) {
    return Math.floor(totalPages);
  }
  return undefined;
};

export async function loadAllPages<T>(
  loadPage: PageLoader<T>,
  options: QueryOptions = {},
  keySelector: PageKeySelector<T> = defaultKeySelector,
): Promise<T[]> {
  const collected: T[] = [];
  const seen = new Set<string>();

  const append = (items: T[]): void => {
    for (const item of items) {
      const key = keySelector(item);
      if (key) {
        if (seen.has(key)) continue;
        seen.add(key);
      }
      collected.push(item);
    }
  };

  // First page always uses 100 rows while preserving caller search/status/sort/order.
  const first = await loadPage({ ...options, page: 1, pageSize: PAGE_ALL_PAGE_SIZE });
  const firstItems = first?.items || [];
  append(firstItems);

  // Defensive totalPages handling: only trust a declared page count when it is
  // usable; otherwise assume a single page unless a full page came back AND the
  // reported total indicates there is more to read.
  const declared = declaredPageCount(first);
  const inferredFromTotal =
    firstItems.length >= PAGE_ALL_PAGE_SIZE && typeof first?.total === "number" && first.total > firstItems.length
      ? Math.max(Math.ceil(first.total / PAGE_ALL_PAGE_SIZE), 2)
      : 1;
  let plannedPages = Math.min(Math.max(declared ?? inferredFromTotal, 1), PAGE_ALL_MAX_PAGES);

  let page = 2;
  while (page <= plannedPages && page <= PAGE_ALL_MAX_PAGES) {
    const result = await loadPage({ ...options, page, pageSize: PAGE_ALL_PAGE_SIZE });
    const items = result?.items || [];
    if (items.length === 0) break;
    append(items);

    // A later page may declare a larger total than the first page reported.
    const laterDeclared = declaredPageCount(result);
    if (laterDeclared && laterDeclared > plannedPages) {
      plannedPages = Math.min(laterDeclared, PAGE_ALL_MAX_PAGES);
    }
    page += 1;
  }

  return collected;
}
